import { Add } from "@mui/icons-material";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
import BrowserUpdatedOutlinedIcon from "@mui/icons-material/BrowserUpdatedOutlined";
import { Autocomplete, Box, Button, Checkbox, FormControl, Grid, InputLabel, MenuItem, OutlinedInput, Select, styled, TextField, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { exportInvoices } from "api/services/billing/invoices";
import SearchContainer from "components/SearchContainer";
import BillingEntityOption from "components/BillingEntityOption";
import { snack } from "components/toast";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { handleError } from "utils/handleError";
import Actions from "./Actions";
import ValidateAccess from "components/ValidateAccess";
import { Permissions } from "data/permissons";
import { ResType } from "types";
import { getBillingEntity } from "api/services/billingEntity";
import { getOrganization } from "api/services/organization";
import { filterProps } from "recharts/types/util/types";
import ExportActions from "./ExportActions";
import { getCurrentFormattedDateTime } from "utils";
import youtubeLogo from "./../../../assets/images/youtube-logo.png";
import excelIcon from "./../../../assets/images/128px-Microsoft_Office_Excel_(2019–2025).svg.png";
import Announcement from "./../../../assets/images/Announcement.svg";
import TdsAnnouncement from "./TdsAnnouncement";
import DateRange from "./DateRange";
import SyncIcon from "@mui/icons-material/Sync";
import SyncToTallyDialog from "views/billing/SyncToTally/SyncToTallyDialog";



const StyledBox = styled(Box) <{ isMobile: boolean }>`
  display: ${({ isMobile }) => (isMobile ? "block" : "flex")};
  justify-content: ${({ isMobile }) => (isMobile ? "initial" : "space-between")};
  align-items: center;
  gap: ${({ isMobile }) => (isMobile ? "1rem" : "initial")};

  padding-right: 16px;
  margin-bottom: 16px;
  ${({ isMobile }) => isMobile && `
    // margin-left: 16px;  
    // margin-top: 16px;
  `}
`;

const StyledButtonContainer = styled(Box) <{ isMobile: boolean }>`
  display: ${({ isMobile }) => (isMobile ? "block" : "flex")};
  gap: 8px;
  ${({ isMobile }) => isMobile && `
    button {
      margin-left: 16px;
      margin-top: 8px;
    }
  `}
`;

interface IProps {
  selected?: any[];
  search: string;
  setSearch: (v: string) => void;
  clearSelection?: () => void;
  filters: any;
  setFilters: (V: any) => void;
  setPage: (v: number) => void;
  dates: any;
  setDates: (v: any) => void;
  sort: any;
  gst?: boolean;
  gstTds?: boolean;
  tds?: boolean;
  handleExport?: () => void;
  isExporting?: boolean;
  placeHolder?: string;
  showAnnouncement?: boolean;
}

function InvoicesHeader(props: IProps) {
  const { selected,
    search,
    setSearch,
    clearSelection,
    filters,
    setFilters,
    dates,
    setDates,
    sort,
    setPage,
    gst = false,
    gstTds = false,
    tds = false,
    handleExport: customExport,
    isExporting: customIsExporting,
    placeHolder, showAnnouncement } = props;
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorEl2, setAnchorEl2] = useState<null | HTMLElement>(null);
  const [filterfields, setFilterfields] = useState([
    { type: 'multiSelect', label: 'Billing Entity', name: 'billingEntity', options: [], optionName: 'tradeName' }
  ]);
  const [type, setType] = useState('')
  const [tallyOpen, setTallyOpen] = useState(false);

  const { data: organizationData }: ResType = useQuery(["organization"], getOrganization);
  const isTallySyncEnabled = organizationData?.data?.isTallySyncEnabled;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [openInfo, setOpenInfo] = useState(false);


  const { data: clients, isLoading: billingLoading }: ResType = useQuery(
    ["billing-entites", {
      gst: gst ? true : false,

    }],
    getBillingEntity,
    {
      onSuccess: (res: any) => {
        const filterfieldsstate: any = JSON.parse(JSON.stringify(filterfields));
        filterfieldsstate.forEach((field: any) => {
          if (field.type == 'multiSelect') {
            field.options = res?.data?.sort((a, b) => a.tradeName.localeCompare(b.tradeName));
          }
        });
        setFilterfields(filterfieldsstate);
      }
    }
  );

  const { mutate, isLoading } = useMutation(exportInvoices, {
    onSuccess: (res: any) => {
      const arr = new Uint8Array(res.data?.file?.data);
      const blob = new Blob([arr], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const pdf = window.URL.createObjectURL(blob);
      const formattedDateTime = getCurrentFormattedDateTime();
      let link = document.createElement("a");
      link.href = pdf;
      link.download = (res.data?.type == 'Type-A') ? `ATOM - Invoices based on Line Item ${formattedDateTime}` : (res.data?.type == 'Type-B') ? `ATOM - Invoices based on Tax Rate ${formattedDateTime}` : `ATOM - Invoices Summary ${formattedDateTime}`;

      link.click();
      snack.success("Invoice(s) List Exported to Excel");
    },
    onError: (err: any) => {
      snack.error(handleError(err));
    },
  });

  const handleExport = (type: string) => {
    if (isLoading) return;
    mutate({ data: { ...filters, search, sort, ...dates, type } });
  };

  const handleCategoryChange = (e: any) => {
    setFilters({
      ...filters,
      status: e.target.value
    });
    setPage(0);
  };

  const handleChange = (v: any, value?: any, field?: any) => {
    setFilters({ ...filters, ['' + field.name]: value });
  }

  const status = [
    { id: "APPROVAL_PENDING", name: "Created" },
    { id: "PARTIALLY_PAID", name: "Partially Paid" },
    { id: 'PARTIALLY_PAID_OVERDUE', name: 'Partially Paid - Overdue' },
    { id: "PAID", name: "Paid" },
    { id: "OVERDUE", name: "Overdue" },
    { id: "CANCELLED", name: "Cancelled" },
    { id: "SETTLED", name: "Settled" },

  ];
  const statusWithOutCancleWithSettled = [
    { id: "ISSUED", name: "Issued" },
    { id: "APPROVAL_PENDING", name: "Created" },
    { id: "PARTIALLY_PAID", name: "Partially Paid" },
    { id: 'PARTIALLY_PAID_OVERDUE', name: 'Partially Paid - Overdue' },
    { id: "PAID", name: "Paid" },
    { id: "OVERDUE", name: "Overdue" },
    { id: "SETTLED", name: "Settled" },
  ];

    const statusWithOutCancle = [
    { id: "APPROVAL_PENDING", name: "Created" },
    { id: "PARTIALLY_PAID", name: "Partially Paid" },
    { id: 'PARTIALLY_PAID_OVERDUE', name: 'Partially Paid - Overdue' },
    { id: "PAID", name: "Paid" },
    { id: "OVERDUE", name: "Overdue" },
  ];

  const typeOptions = [
    { id: "INV_B2B", name: "INV (B2B)" },
    { id: "INV_B2C", name: "INV (B2C)" },
    { id: "CRN_B2B", name: "CRN (B2B)" },
    { id: 'CRN_B2C', name: 'CRN (B2C)' },
  ];



  return (
    <>
      <StyledBox isMobile={isMobile}>
        <SearchContainer debounced value={search}
          placeHolder={placeHolder || "Search by Client Name/Client Group (GSTIN) | Invoice #"} onChange={setSearch} minWidth={isMobile ? "200px" : "250px"} />
        <StyledButtonContainer isMobile={isMobile} sx={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end', flex: 1 }}>

          <Box marginLeft={"7px"} marginTop={"3px"} >
            <DateRange dates={dates} setDates={setDates} setPage={setPage} gst={gst ? gst : tds ? tds : gstTds ? gstTds : false} />
          </Box>
          <Box width={220} marginTop={"3px"} >
            {filterfields?.map((field: any, index) => (
              <Grid key={index} item xs={12} sm={6} md={4} lg={3}>

                {(field.type === "multiSelect" && field.label === 'Billing Entity') && (
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small" sx={{ flex: 1, maxWidth: "41vw" }}>
                      <InputLabel id="demo-multiple-checkbox-label">{field?.label}</InputLabel>
                      <Select
                        multiple
                        value={filters.billingEntity}
                        input={<OutlinedInput label={field.label} />}
                        renderValue={(selected) => {
                          return field.options
                            ?.filter((item: any) => selected.includes(item?.id))
                            .map((item: any) => item.tradeName)
                            .join(", ");
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: "300px",
                            },
                          },
                        }}
                        sx={{ maxWidth: "41vw" }}
                      >
                        <Box display={"flex"} alignItems={"center"} m={1}>
                          <Checkbox
                            checked={field?.options?.length === filters?.billingEntity?.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFilters({
                                  ...filters,
                                  billingEntity: field?.options?.map((item: any) => item?.id),
                                });
                                setPage(0)
                              } else {
                                setFilters({ ...filters, billingEntity: [] });
                                setPage(0);
                              }
                            }}
                            sx={{ marginRight: "10px" }}
                          />
                          <Typography> Select All </Typography>
                        </Box>
                        {field?.options?.map((item: any) => (
                          <Box display={"flex"} alignItems={"center"} m={1}>
                            <Checkbox
                              checked={filters.billingEntity.indexOf(item?.id) > -1}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  if (filters.billingEntity.includes(item.id)) {
                                    const billingList = filters.billingEntity.filter(
                                      (eachitem) => eachitem !== item.id
                                    );
                                    setFilters({
                                      ...filters,
                                      billingEntity: [...billingList],
                                    });
                                    setPage(0);
                                  } else {
                                    setFilters({
                                      ...filters,
                                      billingEntity: [...filters.billingEntity, item.id],
                                    });
                                    setPage(0);
                                  }
                                } else {
                                  const billingList = filters.billingEntity.filter((eachitem) => eachitem !== item.id);
                                  setFilters({
                                    ...filters,
                                    billingEntity: [...billingList],
                                  });
                                  setPage(0);
                                }
                              }}
                              sx={{ marginRight: "10px" }}
                            />
                            <BillingEntityOption name={<Typography>{item?.tradeName}</Typography>} hasGst={item?.hasGst} />
                          </Box>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

              </Grid>
            ))}
          </Box>
          <Box display="flex" marginTop={"3px"}>
            {gst && <FormControl fullWidth size="small" sx={{ minWidth: 150, width: isMobile ? "100%" : 200, mb: isMobile ? 1 : 0, mr: 2 }}>
              <InputLabel id="status-multiple-checkbox-label">Type</InputLabel>
              <Select
                multiple
                labelId="status-multiple-checkbox-label"
                value={filters?.type || []}
                input={<OutlinedInput label="Type" />}
                renderValue={(selected) => {
                  const options = typeOptions;
                  return options
                    ?.filter((item: any) => selected.includes(item?.id))
                    .map((item: any) => item.name)
                    .join(", ");
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: "300px",
                    },
                  },
                }}
              >
                <Box display={"flex"} alignItems={"center"} m={1}>
                  <Checkbox
                    checked={(typeOptions)?.length === filters?.type?.length}
                    onChange={(e) => {
                      const options = typeOptions;
                      if (e.target.checked) {
                        setFilters({
                          ...filters,
                          type: options?.map((item: any) => item?.id),
                        });
                        setPage(0)
                      } else {
                        setFilters({ ...filters, type: [] });
                        setPage(0);
                      }
                    }}
                    sx={{ marginRight: "10px" }}
                  />
                  <Typography> Select All </Typography>
                </Box>
                {(typeOptions)?.map((item: any) => (
                  <Box display={"flex"} alignItems={"center"} m={1}>
                    <Checkbox
                      checked={(filters?.type || []).indexOf(item?.id) > -1}
                      onChange={(e) => {
                        const currentType = filters?.type || [];
                        if (e.target.checked) {
                          if (currentType.includes(item.id)) {
                            const typeList = currentType.filter(
                              (eachitem) => eachitem !== item.id
                            );
                            setFilters({
                              ...filters,
                              type: [...typeList],
                            });
                            setPage(0);
                          } else {
                            setFilters({
                              ...filters,
                              type: [...currentType, item.id],
                            });
                            setPage(0);
                          }
                        } else {
                          const typeList = currentType.filter((eachitem) => eachitem !== item.id);
                          setFilters({
                            ...filters,
                            type: [...typeList],
                          });
                          setPage(0);
                        }
                      }}
                      sx={{ marginRight: "10px" }}
                    />
                    <Typography>{item?.name}</Typography>
                  </Box>
                ))}
              </Select>
            </FormControl>}
            <FormControl fullWidth size="small" sx={{ minWidth: 150, width: isMobile ? "100%" : 200, mb: isMobile ? 1 : 0 }}>
              <InputLabel id="status-multiple-checkbox-label">Status</InputLabel>
              <Select
                multiple
                labelId="status-multiple-checkbox-label"
                value={filters.status}
                input={<OutlinedInput label="Invoice Status" />}
                renderValue={(selected) => {
                  const options = props.selected ? status : statusWithOutCancle;
                  return options
                    ?.filter((item: any) => selected.includes(item?.id))
                    .map((item: any) => item.name)
                    .join(", ");
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: "300px",
                    },
                  },
                }}
              >
                <Box display={"flex"} alignItems={"center"} m={1}>
                  <Checkbox
                    checked={(props.selected ? status : statusWithOutCancle)?.length === filters?.status?.length}
                    onChange={(e) => {
                      const options = props.selected ? status : statusWithOutCancle;
                      if (e.target.checked) {
                        setFilters({
                          ...filters,
                          status: options?.map((item: any) => item?.id),
                        });
                        setPage(0)
                      } else {
                        setFilters({ ...filters, status: [] });
                        setPage(0);
                      }
                    }}
                    sx={{ marginRight: "10px" }}
                  />
                  <Typography> Select All </Typography>
                </Box>
                {(props.selected ? status : gst ? statusWithOutCancleWithSettled : statusWithOutCancle)?.map((item: any) => (
                  <Box display={"flex"} alignItems={"center"} m={1}>
                    <Checkbox
                      checked={filters.status.indexOf(item?.id) > -1}
                      onChange={(e) => {
                        if (e.target.checked) {
                          if (filters.status.includes(item.id)) {
                            const statusList = filters.status.filter(
                              (eachitem) => eachitem !== item.id
                            );
                            setFilters({
                              ...filters,
                              status: [...statusList],
                            });
                            setPage(0);
                          } else {
                            setFilters({
                              ...filters,
                              status: [...filters.status, item.id],
                            });
                            setPage(0);
                          }
                        } else {
                          const statusList = filters.status.filter((eachitem) => eachitem !== item.id);
                          setFilters({
                            ...filters,
                            status: [...statusList],
                          });
                          setPage(0);
                        }
                      }}
                      sx={{ marginRight: "10px" }}
                    />
                    <Typography>{item?.name}</Typography>
                  </Box>
                ))}
              </Select>
            </FormControl>
            {selected && (
              <>

                <Button
                  endIcon={<ArrowDropDownOutlinedIcon />}
                  color="primary"
                  variant="outlined"
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  sx={{ ml: 1, maxHeight: "40px" }}
                >
                  Actions
                </Button>
              </>
            )}
          </Box>
          {selected && <ValidateAccess name={Permissions.CREATE_INVOICE}>
            <Button
              onClick={() => navigate("/billing/invoices/add")}
              variant="outlined"
              color="secondary"
              startIcon={<Add />}
              sx={{ maxHeight: "40px", marginTop: "3px" }}
            >
              Invoice
            </Button>

          </ValidateAccess>}

          <ValidateAccess name={Permissions.EXPORT_INVOICES_DOWNLOAD}>
            <Tooltip title={isLoading || customIsExporting ? "Exporting..." : "Data Export to Excel"}>
              <img
                src={excelIcon}
                alt="Export"
                style={{
                  cursor: (isLoading || customIsExporting) ? "not-allowed" : "pointer",
                  width: 35,
                  height: 35,
                  opacity: (isLoading || customIsExporting) ? 0.5 : 1,
                  pointerEvents: (isLoading || customIsExporting) ? "none" : "auto",
                }}
                onClick={(e) => customExport ? customExport() : setAnchorEl2(e.currentTarget)}
                aria-disabled={isLoading || customIsExporting}
              />
            </Tooltip>
          </ValidateAccess>
{!gst && !tds && (
  <>
          {isTallySyncEnabled && (
            <Tooltip title="Sync to Tally">
              <Button
                onClick={() => setTallyOpen(true)}
                variant="outlined"
                color="secondary"
                startIcon={<SyncIcon />}
                sx={{ maxHeight: "40px", marginTop: "3px" }}
              >
                Sync to Tally
              </Button>
            </Tooltip>
          )}
          {isTallySyncEnabled && (
            <SyncToTallyDialog open={tallyOpen} setOpen={setTallyOpen} />
          )}
          </>
)}
          {showAnnouncement && <Button
            onClick={() => setOpenInfo(true)}
            sx={{
              backgroundColor: "#fff",
              "&:hover": { backgroundColor: "#fff" },
              p: 0,
              minWidth: "auto",
            }}
          >
            <Tooltip title="Announcement">
              <img
                src={Announcement}
                alt="Announcement"
                style={{ cursor: "pointer", width: 33, height: 33 }}
              />
            </Tooltip>
          </Button>}
          <TdsAnnouncement open={openInfo} setOpen={setOpenInfo} />
        </StyledButtonContainer>
      </StyledBox>
      {clearSelection && selected && (
        <Actions
          clearSelection={clearSelection}
          selected={selected}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
        />
      )}
      {clearSelection && selected && (
        <ExportActions
          anchorEl={anchorEl2}
          setAnchorEl={setAnchorEl2}
          handleExport={handleExport}
          setType={setType}
        />
      )}
    </>
  );
}

export default InvoicesHeader;
